import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const REF_LABELS = {
  quotations: "Quotations",
  drafts: "Drafts",
  invoiced_quotations: "Invoiced / dispatch quotations",
  mcf_stock_transfers: "MCF Stock Transfers",
  warehouse_inventory: "FBW stock records",
  inventory_allocations: "Inventory allocation records",
};

const detailMsg = (err, fb) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fb;
};

// Admin delete flow: references check → permanent delete, or deactivate if in use.
export function WarehouseDeleteDialog({ warehouse, onClose, onDone }) {
  const [refs, setRefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    setRefs(null);
    if (!warehouse) return;
    axios.get(`${API}/warehouses/${warehouse.id}/references`, { withCredentials: true })
      .then((r) => setRefs(r.data.references))
      .catch((e) => { toast.error(detailMsg(e, "Could not check warehouse usage")); closeRef.current(); });
  }, [warehouse]);

  if (!warehouse) return null;
  const inUse = refs && Object.keys(refs).length > 0;
  const label = `${warehouse.warehouse_name} (${warehouse.warehouse_code})`;

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.references) setRefs(d.references);
      toast.error(detailMsg(e, "Action failed"));
    } finally {
      setBusy(false);
    }
  };
  const doDelete = () => run(() => axios.delete(`${API}/warehouses/${warehouse.id}`, { withCredentials: true }), `${label} deleted`);
  const doDeactivate = () => run(() => axios.put(`${API}/warehouses/${warehouse.id}`, { is_active: false }, { withCredentials: true }), `${label} deactivated`);

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="warehouse-delete-dialog">
        <DialogHeader>
          <DialogTitle>{inUse ? "Warehouse is in use" : "Delete warehouse?"}</DialogTitle>
          <DialogDescription data-testid="warehouse-delete-target">{label}</DialogDescription>
        </DialogHeader>
        {!refs ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Checking linked records…</p>
        ) : inUse ? (
          <div className="space-y-3 text-sm" data-testid="warehouse-in-use-message">
            <p className="flex gap-2 text-amber-900"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />This warehouse is referenced by existing records, so it can't be permanently deleted. No data will be removed.</p>
            <ul className="rounded-md border bg-slate-50 divide-y">
              {Object.entries(refs).map(([k, n]) => (
                <li key={k} className="flex justify-between px-3 py-1.5" data-testid={`warehouse-ref-${k}`}><span>{REF_LABELS[k] || k}</span><span className="font-medium">{n}</span></li>
              ))}
            </ul>
            <p className="text-muted-foreground">
              {warehouse.is_active
                ? "Deactivate it instead: it will be hidden from dropdowns for new transactions, while all historical records keep their warehouse reference."
                : "It is already inactive and hidden from new transactions."}
            </p>
          </div>
        ) : (
          <p className="text-sm">No records reference this warehouse. It will be permanently deleted. This action cannot be undone.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="warehouse-delete-cancel">Cancel</Button>
          {refs && inUse && warehouse.is_active && (
            <Button onClick={doDeactivate} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="warehouse-deactivate-confirm">Deactivate</Button>
          )}
          {refs && !inUse && (
            <Button variant="destructive" onClick={doDelete} disabled={busy} data-testid="warehouse-delete-confirm">Delete</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
